// EventHistoryPage.js
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { Box, Typography } from '@mui/material';
import { EventBusy } from '@mui/icons-material';
import {
  Table, ConfigProvider, Select, Card, Tooltip, Button, Modal,
  Checkbox, Form, Popover, Radio, Input, Dropdown,
  Space, Tabs, Tag, List,
  message, notification,
} from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined, SearchOutlined, ExportOutlined, DownloadOutlined, RedoOutlined, StopOutlined, CloseOutlined, CheckOutlined, WarningOutlined } from '@ant-design/icons';
import SideMenu from '../components/SideMenu';
import {
  fetchSaltDispatchRoutes,
  fetchSnowDispatchRoutes,
  fetchEventSiteHistory,
  reDispatchSaltRoute,
  reDispatchSnowRoute,
  cancelSaltDispatchSites,
  cancelSnowDispatchSites,
  evaluateSiteCancellations,
  evaluateArchiveRoute,
  archiveRoute,
  updateSaltRouteSubcontractorAcknowledgement,
  updateSnowSiteSubcontractorAcknowledgement,
  fetchEventHistoryCSV
} from '../api';
import { useEvents } from '../context/EventContext';
import { useConstants } from '../context/ConstantsContext';
import { useUser } from '../context/UserContext';
import { getFormattedDate, formatUtcDateTime } from '../utils/dateUtils';
import { displayResultNotifications } from '../utils/notificationUtils';
import '../styles/ButtonStyles.css'
import '../styles/CustomCheckbox.css'

const StyledTable = styled(Table)`
  /* Inactive routes (Grey background) */
  .ant-table-tbody > tr.is-inactive {
    background-color: #f0f0f0;
    color: #b0b0b0;
    &:hover > td {
      background-color: #f0f0f0;
    }
  }

  /* Ensure text contrast is appropriate for readability */
  .ant-table-tbody > tr.is-inactive td {
    color: #7a7a7a; /* Darker grey for contrast */
  }

  /* Selected rows (Orange background) */
  .ant-table-tbody > tr.selected-row {
    background-color: #fdebd0;
    &:hover > td {
      background-color: #fdebd0;
    }
  }

  /* Ensure cancelled sites remain grey even when selected */
  .ant-table-tbody > tr.selected-row.is-cancelled,
  .ant-table-tbody > tr.is-cancelled.selected-row {
    background-color: #e0e0e0;
    color: #a0a0a0;
    &:hover > td {
      background-color: #e0e0e0;
    }
  }

  /* Expanded row background remains orange */
  .ant-table-tbody > tr.selected-row + tr.ant-table-expanded-row {
    background-color: #fdebd0;
  }

  /* Nested table rows inside expanded rows should also be highlighted */
  .ant-table-tbody > tr.selected-row + tr.ant-table-expanded-row .ant-table-container .ant-table-tbody > tr > td {
    background-color: #fdebd0;
  }

  /* Keep hover background consistent */
  .ant-table-tbody > tr.selected-row + tr.ant-table-expanded-row .ant-table-container .ant-table-tbody > tr:hover > td {
    background-color: #fdebd0;
  }

  /* Style child table header when parent row is selected */
  .ant-table-tbody > tr.selected-row + tr.ant-table-expanded-row .ant-table-container .ant-table-thead th {
    background-color: #fdebd0;
  }

  /* Cancelled sites should be greyed out */
  .ant-table-tbody > tr.is-cancelled {
    background-color: #e0e0e0; /* Light grey */
    color: #a0a0a0;
    &:hover > td {
      background-color: #e0e0e0;
  }

  /* Ensure cancelled sites remain grey even when the parent route is selected */
  .ant-table-tbody > tr.selected-row + tr.ant-table-expanded-row .ant-table-container .ant-table-tbody > tr.is-cancelled > td {
    background-color: #e0e0e0;
  }

  /* Ensure text contrast is appropriate for readability */
  .ant-table-tbody > tr.is-cancelled td {
    color: #7a7a7a; /* Darker grey for contrast */
  }
`;

const EventHistoryPage = () => {
  const constants = useConstants();
  const { user } = useUser();
  const { allEvents, selectedEventId, selectedEvent, selectEvent, isLoading: eventsLoading } = useEvents();

  const [eventDispatchesLoading, setEventDispatchesLoading] = useState(false);
  const [eventSitesLoading, setEventSitesLoading] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [dispatchRoutes, setDispatchRoutes] = useState([]);
  const [filteredDispatchRoutes, setFilteredDispatchRoutes] = useState([]);
  const [selectedDispatchRoute, setSelectedDispatchRoute] = useState(null);
  const [eventSiteHistories, setEventSiteHistories] = useState([]);
  const [filteredSiteHistories, setFilteredSiteHistories] = useState([]);

  const [selectedSitesRouteId, setSelectedSitesRouteId] = useState(null);
  const [selectedSites, setSelectedSites] = useState([]);

  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelSitesOpen, setCancelSitesOpen] = useState(false);
  const [cancelSitesWillArchiveSubs, setCancelSitesWillArchiveSubs] = useState([]);
  const [cancelSitesWillArchivePlural, setCancelSitesWillArchivePlural] = useState([]);
  const [cancelSitesForm] = Form.useForm();

  const [redispatchLoading, setRedispatchLoading] = useState(false);
  const [redispatchConfirmOpen, setRedispatchConfirmOpen] = useState(false);

  const [archiveLoading, setArchiveLoading] = useState(false);
  const [archiveConfirmOpen, setArchiveConfirmOpen] = useState(false);
  const [archiveEvaluationData, setArchiveEvaluationData] = useState({ subcontractors: [], numSubcontractors: 0, sites: [], numSites: 0 });
  const [archiveForm] = Form.useForm();

  const [messageApi, contextHolder] = message.useMessage();
  const [notificationApi, notifContextHolder] = notification.useNotification();

  // Initialize event selection from context
  useEffect(() => {
    if (allEvents.length > 0 && !selectedEventId) {
      // Select first event if none selected
      selectEvent(allEvents[0].event_id);
    }
  }, [allEvents, selectedEventId, selectEvent]);

  const loadDispatchRoutes = useCallback(async () => {
    if (!selectedEvent) return;
    setEventDispatchesLoading(true);
    try {
      const fetchRoutes = selectedEvent.event_type === constants.event_type.snow ? fetchSnowDispatchRoutes : fetchSaltDispatchRoutes;
      const routesData = await fetchRoutes(selectedEvent.event_id);
      setDispatchRoutes(routesData);
    } catch (error) {
      messageApi.error('Error fetching dispatch routes');
    } finally {
      setEventDispatchesLoading(false);
    }
  }, [selectedEvent, constants, messageApi]);

  const loadEventSiteHistory = useCallback(async () => {
    if (!selectedEvent) return;
    setEventSitesLoading(true);
    try {
      const sitesData = await fetchEventSiteHistory(selectedEvent.event_id);
      console.log('sitesData:', sitesData);
      setEventSiteHistories(sitesData);
    } catch (error) {
      messageApi.error('Error fetching event site history');
    } finally {
      setEventSitesLoading(false);
    }
  }, [selectedEvent, messageApi]);

  useEffect(() => {
    loadDispatchRoutes();
    loadEventSiteHistory();
  }, [selectedEvent, loadDispatchRoutes, loadEventSiteHistory]);

  // Filter dispatch routes based on search query
  useEffect(() => {
    const filteredData = dispatchRoutes.filter(route => {
      const routeName = route.route_name || '';
      const subcontractorName = selectedEvent.event_type === constants.event_type.salt
        ? route.subcontractor_name || ''
        : '';
      const siteMatch = route.sites?.some(site => {
        const siteNameMatch = site.site_name.toLowerCase().includes(searchQuery.toLowerCase());
        const siteSubcontractorMatch = selectedEvent.event_type === constants.event_type.snow
          ? site.subcontractor_name?.toLowerCase().includes(searchQuery.toLowerCase())
          : false;
        return siteNameMatch || siteSubcontractorMatch;
      });
      return (
        routeName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        subcontractorName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        siteMatch
      );
    });
    setFilteredDispatchRoutes(filteredData);

    const filteredSiteHistories = eventSiteHistories.filter(site => {
      const siteNameMatch = site.site_name.toLowerCase().includes(searchQuery.toLowerCase());
      const clientNameMatch = site.client_name.toLowerCase().includes(searchQuery.toLowerCase());
      const locationMatch = site.city.toLowerCase().includes(searchQuery.toLowerCase());
      return siteNameMatch || clientNameMatch || locationMatch;
    });
    setFilteredSiteHistories(filteredSiteHistories);
  }, [searchQuery, dispatchRoutes, selectedEvent, constants, eventSiteHistories]);

  const handleEventChange = (value) => {
    setSearchQuery('');
    selectEvent(value);
  };

  const confirmRedispatch = () => {
    setRedispatchConfirmOpen(true);
  };

  const handleRedispatch = async () => {
    if (selectedDispatchRoute) {
      try {
        setRedispatchLoading(true);
        (selectedEvent.event_type === constants.event_type.snow)
          ? await reDispatchSnowRoute(selectedDispatchRoute.dispatch_route_id)
          : await reDispatchSaltRoute(selectedDispatchRoute.dispatch_route_id);
        messageApi.success('Redispatch route created; it can be be dispatched again from the Dispatch Routes Page!');
        const updatedRoutes = (selectedEvent.event_type === constants.event_type.snow)
          ? await fetchSnowDispatchRoutes(selectedEvent.event_id)
          : await fetchSaltDispatchRoutes(selectedEvent.event_id);
        setDispatchRoutes(updatedRoutes);
        setSelectedDispatchRoute(null);
      } catch (error) {
        messageApi.error('Failed to create redispatch route');
      } finally {
        setRedispatchConfirmOpen(false);
        setRedispatchLoading(false);
      }
    }
  };

  const evaluateArchive = async () => {
    if (!selectedDispatchRoute) return;
  
    try {
      const payload = selectedEvent?.event_type === constants?.event_type.snow
        ? { snow_dispatch_route_id: selectedDispatchRoute.dispatch_route_id }
        : { salt_dispatch_route_id: selectedDispatchRoute.dispatch_route_id };
        
      const response = await evaluateArchiveRoute(payload);
  
      setArchiveEvaluationData({
        subcontractors: response?.subcontractors || [],
        numSubcontractors: response?.subcontractors.length,
        sites: response?.sites || [],
        numSites: response?.sites.length,

      });
    } catch (error) {
      console.error('Error evaluating archive route:', error);
      messageApi.error('Failed to fetch archival evaluation data');
      setArchiveEvaluationData({ subcontractors: [], sites: [] });
    }
  };
  
  const confirmArchive = async () => {
    if (!selectedDispatchRoute) {
      messageApi.error("No route selected for archiving.");
      return;
    }
    try {
      await evaluateArchive();
      setArchiveConfirmOpen(true);
    } catch (error) {
      console.error("Error during archive preparation:", error);
      messageApi.error("Failed to prepare archive confirmation.");
    }
  };

  const handleArchive = async () => {
    if (selectedDispatchRoute) {
      try {
        setArchiveLoading(true);

        const values = await archiveForm.validateFields();
        const sendCancellationLetter = values.sendCancellationNotice || false;

        const payload = selectedEvent.event_type === constants.event_type.snow
          ? {
              snow_dispatch_route_id: selectedDispatchRoute.dispatch_route_id,
              send_cancellation_letter: sendCancellationLetter,
              user_email: user.email,
            }
          : {
              salt_dispatch_route_id: selectedDispatchRoute.dispatch_route_id,
              send_cancellation_letter: sendCancellationLetter,
              user_email: user.email,
            };
        
        const result = await archiveRoute(payload);
        displayResultNotifications(result, 'Route Cancellation', notificationApi);

        const updatedRoutes = (selectedEvent.event_type === constants.event_type.snow)
          ? await fetchSnowDispatchRoutes(selectedEvent.event_id)
          : await fetchSaltDispatchRoutes(selectedEvent.event_id);

        setDispatchRoutes(updatedRoutes);
        setSelectedDispatchRoute(null);
      } catch (error) {
        const errorMessage = error.message || 'Failed to archive the route';
        messageApi.error(errorMessage);
        console.error(error);
      } finally {
        setArchiveConfirmOpen(false);
        setArchiveLoading(false);
      }
    }
  };
  
  const allSelectedSitesNotCancelled = useMemo(() => {
    if (!selectedSitesRouteId || selectedSites.length === 0) {
      return false;
    }
  
    // Find the active route object
    const activeRoute = dispatchRoutes.find(
      (r) => r.dispatch_route_id === selectedSitesRouteId
    );
    if (!activeRoute) {
      return false;
    }
  
    // Return true if at least one site is selected
    // AND all have `cancelled_at === null` (or undefined).
    return (
      selectedSites.length > 0 &&
      selectedSites.every((s) => !s.cancelled_at)
    );
  }, [selectedSitesRouteId, selectedSites, dispatchRoutes]);

  const checkIfLastSitesOnRoute = async () => {
    try {
      const payload = {
        [selectedEvent.event_type.toLowerCase()]: {
          dispatch_route_id: selectedSitesRouteId,
          dispatch_site_ids: selectedSites.map((s) => s.id),
        },
      };
      const response = await evaluateSiteCancellations(payload);
      console.log('evaluate cancellations:', response);
      setCancelSitesWillArchiveSubs(response.subcontractors);
      if (cancelSitesWillArchiveSubs.length > 1) {
        setCancelSitesWillArchivePlural('s');
      } else {
        setCancelSitesWillArchivePlural('');
      }
    } catch (error) {
      console.error("Failed to evaluate site cancellations", error);
    }
  };

  const handleCancelSites = async () => {
    checkIfLastSitesOnRoute();
    
    const initialValues = {};
    selectedSites.forEach((site) => {
      initialValues[`sendCancellation_${site.id}`] = false;
    });
    cancelSitesForm.setFieldsValue(initialValues);

    setCancelSitesOpen(true);
  };

  const confirmCancelSites = async () => {
    try {
      setCancelLoading(true);
  
      // Collect all form checkbox values
      const values = await cancelSitesForm.validateFields();
  
      // Prepare the cancellations array
      const cancellations = selectedSites.map((site) => ({
        dispatch_site_id: site.id,
        notify: Boolean(values[`sendCancellation_${site.id}`]),
      }));
  
      if (cancellations.length === 0) {
        messageApi.warning('No sites selected for cancellation.');
        return;
      }
  
      let response;
  
      // Call the correct API based on the event type
      if (selectedEvent.event_type === constants.event_type.snow) {
        response = await cancelSnowDispatchSites(selectedSitesRouteId, cancellations, user.email);
      } else {
        response = await cancelSaltDispatchSites(selectedSitesRouteId, cancellations, user.email);
      }
  
      const { data } = response;
  
      // Handle API response
      if (data.messages && !data.errors) {
        notification.success({
          message: 'Sites Cancelled Successfully',
          description: data.messages.join(<br/>),
          duration: 7,
        });
      } else if (data.messages && data.errors) {
        notification.warning({
          message: 'Sites Cancelled with Warnings',
          description: `Messages:\n${data.messages.join(<br/>)}\nErrors:\n${data.errors.join(<br/>)}`,
          duration: 10,
        });
      } else if (data.errors) {
        notification.error({
          message: 'Failed to Cancel Sites',
          description: data.errors.join(<br/>),
          duration: 10,
        });
      }
  
      const updatedRoutes = selectedEvent.event_type === constants.event_type.snow
        ? await fetchSnowDispatchRoutes(selectedEvent.event_id)
        : await fetchSaltDispatchRoutes(selectedEvent.event_id);
  
      setDispatchRoutes(updatedRoutes);
      setSelectedSites([]);
  
    } catch (error) {
      messageApi.error('An error occurred while cancelling the sites.');
      console.error(error);
    } finally {
      setCancelSitesOpen(false);
      setCancelLoading(false);
      cancelSitesForm.resetFields();
    }
  };

  const updateAcknowledgement = async (record, value) => {
    try {
      if (selectedEvent.event_type === constants.event_type.snow) {
        await updateSnowSiteSubcontractorAcknowledgement(record.snow_dispatch_route_id, record.id, value);
      } else {
        await updateSaltRouteSubcontractorAcknowledgement(record.dispatch_route_id, value);
        const updatedRoutes = await fetchSaltDispatchRoutes(selectedEvent.event_id);
        setDispatchRoutes(updatedRoutes);
      }
      
      message.success('Subcontractor acknowledgement updated successfully');
    } catch (error) {
      message.error('Failed to update acknowledgement');
      console.error(error);
    }
  };

  const AcknowledgementEditor = ({ acknowledgement, record, onUpdate }) => {
    const [ackValue, setAckValue] = useState(acknowledgement);
  
    const handleUpdate = async (newValue) => {
      setAckValue(newValue); 
      await onUpdate(record, newValue);
    };
  
    const content = (
      <div style={{ display: 'flex', flexDirection: 'row' }}>
        <div style={{ marginRight: '10px', cursor: 'pointer' }} onClick={() => handleUpdate(true)}>
          <Radio checked={ackValue === true} /> True
        </div>
        <div style={{ marginLeft: '10px', cursor: 'pointer' }} onClick={() => handleUpdate(false)}>
          <Radio checked={ackValue === false} /> False
        </div>
      </div>
    );
  
    return (
      <Popover
        content={content}
        trigger="click"
        overlayStyle={{ zIndex: 9999 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            width: '100%',
            height: '100%',
          }}
        >
          {ackValue ? (
            <CheckCircleOutlined style={{ color: 'green' }} />
          ) : (
            <CloseCircleOutlined style={{ color: 'red' }} />
          )}
        </div>
      </Popover>
    );
  };

  const renderAcknowledgementColumn = (acknowledgement, record) => (
    <AcknowledgementEditor
      acknowledgement={acknowledgement}
      record={record}
      onUpdate={updateAcknowledgement}
    />
  );

  const handleExportCsvClick = async () => {
    if (!selectedEvent) {
      messageApi.error("Please select an event to export CSV data.");
      return;
    }
    try {
      const csvData = await fetchEventHistoryCSV(selectedEvent.event_id);
      const url = window.URL.createObjectURL(new Blob([csvData], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Event_History_${selectedEvent.event_name}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      messageApi.error("Error downloading event history CSV");
    }
  };

  const exportMenuItems = [
    {
      key: 'csv',
      label: (
        <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <span>CSV</span>
          <DownloadOutlined />
        </span>
      ),
      onClick: handleExportCsvClick,
    },
    // {
    //   key: 'pdf',
    //   label: 'Export as PDF',
    //   onClick: handleExportPdfClick,
    // },
  ]; 

  const siteExportMenuItems = [
    {
      key: 'csv',
      label: (
        <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <span>CSV</span>
          <DownloadOutlined />
        </span>
      ),
      disabled: true
    },
  ]; 
  
  const getStatusIcon = (value) => {
    return value ? (
      <CheckCircleOutlined style={{ color: 'green' }} />
    ) : (
      <CloseCircleOutlined style={{ color: 'red' }} />
    );
  };

  const getBillingStatusIcon = (status) => {
    if (status === constants.status.pending) return <CloseCircleOutlined style={{ color: 'red' }} />;
    if (status === constants.status.awaiting) return <ClockCircleOutlined style={{ color: '#DAA520' }} />;  // (Goldenrod)
    if (status === constants.status.approved) return <CheckCircleOutlined style={{ color: 'green' }} />;
    return '-';
  };

  const formatDateTime = (dispatchedAt) => {
    if (!dispatchedAt) return '-';
    return (
      <span>
        {formatUtcDateTime(dispatchedAt, 'YYYY-MM-DD')}
        <br />
        {formatUtcDateTime(dispatchedAt, 'h:mm A')}
      </span>
    );
  };

  const formatDateTimeSingleLine = (dispatchedAt) => {
    if (!dispatchedAt) return '-';
    return (
      <span>{formatUtcDateTime(dispatchedAt)}</span>
    );
  };

  // Define site details table columns
  const siteColumns = [
    {
      title: <span style={{ fontSize: '12px' }}>Site Name</span>,
      dataIndex: 'site_name',
      key: 'site_name',
      render: (value) => value || 'N/A',
    },
    {
      title: <span style={{ fontSize: '12px' }}>Location</span>,
      dataIndex: 'location',
      key: 'location',
      render: (value) => value || 'N/A',
    },
    (selectedEvent?.event_type === constants?.event_type.snow) && {
      title: <span style={{ fontSize: '12px' }}>Subcontractor</span>,
      dataIndex: 'subcontractor_name',
      key: 'subcontractor_name',
      render: (value) => value || 'N/A',
    },
    {
      title: <span style={{ fontSize: '12px' }}>Notified</span>,
      dataIndex: 'notified_at',
      key: 'notified_at',
      width: '8%',
      align: 'center',
      render: (value) => (
        <Tooltip title={value ? formatUtcDateTime(value) : 'Not Notified'}>
          {getStatusIcon(Boolean(value))}
        </Tooltip>
      ),
    },
    {
      title: <span style={{ fontSize: '12px' }}>Dispatched</span>,
      dataIndex: 'dispatched_at',
      key: 'dispatched_at',
      width: '8%',
      align: 'center',
      filters: [
        { text: 'Yes', value: true },
        { text: 'No', value: false },
      ],
      render: (value) => (
        <Tooltip title={value ? formatUtcDateTime(value) : 'Not Dispatched'}>
          {getStatusIcon(Boolean(value))}
        </Tooltip>
      ),
    },
    (selectedEvent?.event_type === constants?.event_type.snow) && {
      title: <span style={{ fontSize: '12px' }}>Subcontractor Acknowledgement</span>,
      dataIndex: 'subcontractor_acknowledgement',
      key: 'subcontractor_acknowledgement',
      align: 'center',
      render: (acknowledgement, record) =>
        renderAcknowledgementColumn(acknowledgement, record),
    },
    {
      title: <span style={{ fontSize: '12px' }}>Serviced</span>,
      dataIndex: 'serviced_at',
      key: 'serviced_at',
      width: '8%',
      align: 'center',
      render: (value) => (
        <Tooltip title={value ? formatUtcDateTime(value) : 'Not Serviced'}>
          {getStatusIcon(Boolean(value))}
        </Tooltip>
      ),
    },
    {
      title: <span style={{ fontSize: '12px' }}>Invoice</span>,
      dataIndex: 'invoice_status',
      key: 'invoice_status',
      width: '8%',
      align: 'center',
      render: (status) => getBillingStatusIcon(status),
    },
    (selectedEvent?.event_type === constants?.event_type.snow) && {
      title: <span style={{ fontSize: '12px' }}>PO Status</span>,
      dataIndex: 'purchase_order_status',
      key: 'purchase_order_status',
      width: '8%',
      align: 'center',
      render: (status) => getBillingStatusIcon(status),
    },
    {
      title: <span style={{ fontSize: '12px' }}>Cancellation Letter</span>,
      dataIndex: 'cancellation_notified_at',
      key: 'cancellation_notified_at',
      align: 'center',
      width: '10%',
      render: (_, record) => 
        record.cancellation_notified_at ? (
          <Tooltip title={record.cancellation_notified_at ? formatUtcDateTime(record.cancellation_notified_at) : 'Service Cancellation Letter Not Sent'}>
            {getStatusIcon(Boolean(record.cancellation_notified_at))}
          </Tooltip>
        ) : null,
    },
    {
      dataIndex: 'cancelled_at',
      key: 'cancelled_at',
      align: 'center',
      width: '5%',
      render: (cancelled_at) => 
        cancelled_at === null
          ? null
          : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <Tooltip title={"Site has been cancelled."}>
                <EventBusy />
              </Tooltip>
            </div>
          ),
    },
  ].filter(Boolean); // Filter out null/undefined columns

  // Expanded row rendering for dispatch route site details
  const expandedRowRenderSites = (dispatchRoute) => (
    <Table
      columns={siteColumns}
      dataSource={dispatchRoute.sites}
      pagination={false}
      rowKey="id"
      rowClassName={(record) => record.cancelled_at ? 'is-cancelled' : ''}
      rowSelection={{
        selectedRowKeys: (
          selectedSitesRouteId === dispatchRoute.dispatch_route_id
            ? selectedSites.map((s) => s.id)
            : []
        ),
        onChange: (newSelectedKeys, newSelectedRows) => {
          if (selectedSitesRouteId !== dispatchRoute.dispatch_route_id) {
            setSelectedSitesRouteId(dispatchRoute.dispatch_route_id);
          }
          setSelectedSites(newSelectedRows);
        }
      }}
      tableLayout="fixed"
    />
  );

  // Define the main table columns with the required fields
  const routeColumns = [
    {
      title: 'Route Name',
      dataIndex: 'route_name',
      key: 'route_name',
      width: '30%',
      sorter: (a, b) => a.route_name.localeCompare(b.route_name),
    },
    {
      title: 'Dispatched',
      dataIndex: 'dispatched_at',
      key: 'dispatched_at',
      align: 'center',
      filters: [
        { text: 'Yes', value: true },
        { text: 'No', value: false },
      ],
      onFilter: (value, record) => Boolean(record.dispatched_at) === value,
      render: (value) => (
        <Tooltip title={value ? formatUtcDateTime(value) : 'Not Dispatched'}>
          {getStatusIcon(Boolean(value))}
        </Tooltip>
      ),
      sorter: (a, b) => new Date(a.dispatched_at) - new Date(b.dispatched_at),
    },
    {
      title: 'Dispatched By',
      dataIndex: 'dispatched_by',
      key: 'dispatched_by',
      render: (value) => value || '-',
      sorter: (a, b) => (a.dispatched_by || '').localeCompare(b.dispatched_by || ''),
    },
    (selectedEvent?.event_type === constants?.event_type.salt) && {
      title: 'Subcontractor',
      dataIndex: 'subcontractor_name',
      key: 'subcontractor_name',
      render: (value) => value || 'Unassigned',
      sorter: (a, b) => (a.subcontractor_name || '').localeCompare(b.subcontractor_name || ''),
    },
    (selectedEvent?.event_type === constants?.event_type.salt) && {
      title: 'Subcontractor Acknowledgement',
      dataIndex: 'subcontractor_acknowledgement',
      key: 'subcontractor_acknowledgement',
      align: 'center',
      render: (acknowledgement, record) =>
        renderAcknowledgementColumn(acknowledgement, record),
    },
    (selectedEvent?.event_type === constants?.event_type.salt) && {
      title: 'Purchase Order',
      dataIndex: 'purchase_order_status',
      key: 'purchase_order_status',
      align: 'center',
      render: (status) => getBillingStatusIcon(status),
    },
    {
      dataIndex: 'is_active',
      key: 'is_active',
      align: 'center',
      width: '5%',
      render: (is_active) => 
        is_active
          ? null
          : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <Tooltip title={"Route has been cancelled."}>
                <EventBusy />
              </Tooltip>
            </div>
          ),
    },
  ].filter(Boolean); // Filter out null/undefined entries

  const SiteDispatchList = ({ dispatches }) => {
    return (
      <div style={{ width: '500px' }}>
        <List
          dataSource={dispatches}
          renderItem={dispatch => (
            <List.Item>
              <List.Item.Meta
                title={dispatch.route_name}
                description={dispatch.subcontractor_name}
              />
                <div>{formatDateTime(dispatch.dispatched_at)}</div>
            </List.Item>
          )}
        />
      </div>
    )
  };

  const SiteServiceList = ({ serviceHistories }) => {
    return (
      <div style={{ width: '500px' }}>
        <List
          dataSource={serviceHistories}
          renderItem={sh => (
            <List.Item>
              <List.Item.Meta
                title={sh.route_name}
                description={sh.subcontractor_name}
              />
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span>{formatDateTimeSingleLine(sh.started_at)}</span>
                <span> - {formatDateTimeSingleLine(sh.ended_at)}</span>
              </div>
            </List.Item>
          )}
        />
      </div>
    )
  };

  const siteServiceHistoryColumns = [
    {
      title: 'Site',
      dataIndex: 'site_name',
      key: 'site_name',
      width: '35%',
      sorter: (a, b) => a.site_name.localeCompare(b.site_name),
    },
    {
      title: 'Client',
      dataIndex: 'client_name',
      key: 'client_name',
      width: '25%',
      sorter: (a, b) => a.client_name.localeCompare(b.client_name),
    },
    {
      title: 'Location',
      dataIndex: 'city',
      key: 'location',
      sorter: (a, b) => a.city.localeCompare(b.city),
    },
    {
      title: 'Notified',
      dataIndex: 'notified_at',
      key: 'notified_at',
      align: 'center',
      filters: [
        { text: 'Notified', value: true },
        { text: 'Not Notified', value: false },
      ],
      onFilter: (value, record) => Boolean(record.notified_at) === value,
      render: (value) => (
        <Tooltip
          placement='left'
          title={value ? formatUtcDateTime(value) : 'Not Notified'}
          overlayInnerStyle={{ backgroundColor: '#fff', color: '#000' }}
        >
          {value ? <CheckOutlined style={{ color: 'green' }} /> : <WarningOutlined style={{ color: 'red' }} />}
        </Tooltip>
      ),
    },
    {
      title: 'Dispatches',
      dataIndex: 'dispatches',
      key: 'dispatches',
      align: 'center',
      filters: [
        { text: 'Dispatched', value: true },
        { text: 'Not Dispatched', value: false },
      ],
      onFilter: (value, record) => (record.dispatches.length > 0) === value,
      render: (dispatches) => (
        <Tooltip
          placement='left'
          overlayInnerStyle={{
            whiteSpace: 'normal',
            backgroundColor: '#fff',
            color: '#000',
          }}
          overlayStyle={{ maxWidth: 'none' }}
          title={
            dispatches.length === 0 ? (
              'Site has not been dispatched on any routes'
            ) : (
              <SiteDispatchList dispatches={dispatches} />
            )
          }
        >
          {dispatches.length > 0 ? <CheckOutlined style={{ color: 'green' }} /> : <CloseOutlined style={{ color: 'red' }} />}
        </Tooltip>
      ),
    },
    {
      title: 'Services',
      dataIndex: 'service_histories',
      key: 'service_histories',
      align: 'center',
      filters: [
        { text: 'Serviced', value: true },
        { text: 'Not Serviced', value: false },
      ],
      onFilter: (value, record) => (record.service_histories.length > 0) === value,
      render: (service_histories) => (
        <Tooltip
          placement='left'
          overlayInnerStyle={{
            whiteSpace: 'normal',
            backgroundColor: '#fff',
            color: '#000',
          }}
          overlayStyle={{ maxWidth: 'none' }}
          title={
            service_histories.length === 0 ? (
              'Site has not been serviced on any dispatch routes'
            ) : (
              <SiteServiceList serviceHistories={service_histories} />
            )
          }
        >
          {service_histories.length > 0 ? <CheckOutlined style={{ color: 'green' }} /> : <CloseOutlined style={{ color: 'red' }} />}
        </Tooltip>
      ),
    },
  ];

  const [tableHeight, setTableHeight] = useState('100vh');
  const titleBoxRef = useRef(null);
  const tabsRef = useRef(null);
  const actionBoxRef = useRef(null);
  const tableRef = useRef(null);

  useEffect(() => {
    const calculateTableHeight = () => {
      const headerHeight = 72;
      const titleBoxHeight = titleBoxRef.current ? titleBoxRef.current.offsetHeight : 32;
      const tabsHeight = tabsRef.current ? tabsRef.current.offsetHeight : 46;
      const actionBoxHeight = actionBoxRef.current ? actionBoxRef.current.offsetHeight : 32;
      const padding = 16;

      // Calculate height of the table header
      const tableHeaderHeight = tableRef.current ? tableRef.current.querySelector('.ant-table-thead').offsetHeight : 48;

      const totalHeightAroundTableRows =
        headerHeight +
        titleBoxHeight +
        tabsHeight +
        actionBoxHeight +
        padding * 6 +
        tableHeaderHeight;

      setTableHeight(`calc(100vh - ${totalHeightAroundTableRows}px)`);
    };

    if (selectedEvent) {
        calculateTableHeight();
    }

    window.addEventListener('resize', calculateTableHeight);
    return () => window.removeEventListener('resize', calculateTableHeight);
  }, [selectedEvent]);

  return (
    <ConfigProvider theme={{ token: { colorPrimary: '#F57D20', colorLinkHover: '#F57D20', colorLink: '#F57D20' } }}>
      {contextHolder}
      {notifContextHolder}
      <Box display="flex">
        {/* Side Menu */}
        <Box sx={{ width: '250px', flexShrink: 0 }}>
          <SideMenu />
        </Box>
        
        {/* Main Content */}
        <Card
          style={{
            height: 'calc(100vh - 72px - 16px)',
            marginRight: '16px',
            overflow: 'hidden',
            boxShadow: '0 0 10px rgba(0, 0, 0, 0.1)'
          }}
        >

          {/* Header and Event Selection */}
          <Box ref={titleBoxRef} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%'}}>
            <Typography variant="h5" component="div">
              Event History
            </Typography>
            <Select
              value={selectedEventId}
              onChange={handleEventChange}
              placeholder="Select Event"
              style={{ width: '600px' }}
              optionFilterProp="children"
              showSearch
              loading={eventsLoading}
            >
              {allEvents.map((event) => (
                <Select.Option key={event.event_id} value={event.event_id} label={`${event.event_name} - ${event.service_type}`}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>{event.event_name} - {event.service_type}</span>
                    <Space size="small">
                      <span>{getFormattedDate(event.event_start)}</span>
                      <Tag color={event.status === constants.status.active ? 'green' : 'red'} style={{ width: '65px', textAlign: 'center' }}>{event.status}</Tag>
                    </Space>
                  </div>
                </Select.Option>
              ))}
            </Select>
          </Box>

          <Tabs ref={tabsRef} defaultActiveKey='dispatches'>
            <Tabs.TabPane tab="Dispatches" key="dispatches">
              <Box ref={actionBoxRef} display="flex" justifyContent="space-between" alignItems="center" marginTop="16px">
                <Input
                  type="text"
                  placeholder="Search Routes, Sites, Subcontractors"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  style={{ width: '300px' }}
                  prefix={<SearchOutlined style={{ color: "#d9d9d9" }} fontSize="small" />}
                  allowClear
                />

                <Box display="flex" gap="10px">
                  <Button
                    type="primary"
                    disabled={!allSelectedSitesNotCancelled || selectedEvent?.status === constants?.status.inactive}
                    onClick={handleCancelSites}
                    style={{
                      backgroundColor: allSelectedSitesNotCancelled && selectedEvent?.status === constants?.status.active ? '#f57e1f' : '',
                      borderColor: allSelectedSitesNotCancelled && selectedEvent?.status === constants?.status.active ? '#f57e1f' : '',
                      color: allSelectedSitesNotCancelled && selectedEvent?.status === constants?.status.active ? 'white' : '',
                      
                    }}
                  >
                    Cancel Selected Sites <CloseOutlined />
                  </Button>
                  
                  <Button
                    type="primary"
                    disabled={!selectedDispatchRoute || !selectedDispatchRoute.dispatched_at || !selectedDispatchRoute.is_active || selectedEvent?.status === constants?.status.inactive}
                    onClick={confirmArchive}
                    style={{
                      backgroundColor:
                        selectedDispatchRoute && selectedDispatchRoute.dispatched_at && selectedDispatchRoute.is_active && selectedEvent?.status === constants?.status.active ? '#f57e1f' : '', // Orange if route is selected and dispatched
                      borderColor:
                        selectedDispatchRoute && selectedDispatchRoute.dispatched_at && selectedDispatchRoute.is_active && selectedEvent?.status === constants?.status.active ? '#f57e1f' : '', // Orange border if enabled
                      color: selectedDispatchRoute && selectedDispatchRoute.dispatched_at && selectedDispatchRoute.is_active && selectedEvent?.status === constants?.status.active ? 'white' : '', // White text when enabled
                      
                    }}
                  >
                    Archive Selected Route <StopOutlined />
                  </Button>

                  <Button
                    type="primary"
                    disabled={!selectedDispatchRoute || !selectedDispatchRoute.dispatched_at || !selectedDispatchRoute.is_active || selectedEvent?.status === constants?.status.inactive}
                    onClick={confirmRedispatch}
                    style={{
                      backgroundColor:
                        selectedDispatchRoute && selectedDispatchRoute.dispatched_at && selectedDispatchRoute.is_active && selectedEvent?.status === constants?.status.active ? '#f57e1f' : '', // Orange if route is selected and dispatched
                      borderColor:
                        selectedDispatchRoute && selectedDispatchRoute.dispatched_at && selectedDispatchRoute.is_active && selectedEvent?.status === constants?.status.active ? '#f57e1f' : '', // Orange border if enabled
                      color: selectedDispatchRoute && selectedDispatchRoute.dispatched_at && selectedDispatchRoute.is_active && selectedEvent?.status === constants?.status.active ? 'white' : '', // White text when enabled
                      
                    }}
                  >
                    Redispatch Selected Route <RedoOutlined />
                  </Button>

                  <Dropdown menu={{ items: exportMenuItems }} trigger={['click']} disabled={!selectedEvent}>
                    <Button type="primary">
                      Export <ExportOutlined />
                    </Button>
                  </Dropdown>
                </Box>
              </Box>

              {/* Scrollable Table */}
              <Box ref={tableRef} sx={{ overflow: 'hidden', marginTop: '16px' }}>
                <StyledTable
                  columns={routeColumns}
                  dataSource={filteredDispatchRoutes}
                  rowKey="dispatch_route_id"
                  loading={eventDispatchesLoading}
                  expandable={{
                    expandedRowRender: (record) => expandedRowRenderSites(record),
                    
                  }}
                  pagination={false}
                  rowClassName={(record) => {
                    let className = '';
                    if (!record.is_active) {
                      className += ' is-inactive';
                    }
                    if (selectedDispatchRoute?.dispatch_route_id === record.dispatch_route_id) {
                      className += ' selected-row';
                    }
                    return className.trim();
                  }}
                  onRow={(record) => ({
                    onClick: () => {
                      if (selectedDispatchRoute?.dispatch_route_id === record.dispatch_route_id) {
                        setSelectedDispatchRoute(null);
                      } else {
                        setSelectedDispatchRoute(record);
                      }
                    },
                  })}
                  scroll={{ y: tableHeight }}
                  sticky
                />
              </Box>
            </Tabs.TabPane>

            <Tabs.TabPane tab="Service Notifications" key="service-notifications">
              <Box ref={actionBoxRef} display="flex" justifyContent="space-between" alignItems="center" marginTop="16px">
                <Input
                  type="text"
                  placeholder="Search Sites"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  style={{ width: '300px' }}
                  prefix={<SearchOutlined style={{ color: "#d9d9d9" }} fontSize="small" />}
                  allowClear
                />

                <Dropdown menu={{ items: siteExportMenuItems }} trigger={['click']} disabled={!selectedEvent}>
                  <Button type="primary">
                    Export <ExportOutlined />
                  </Button>
                </Dropdown>
              </Box>

              {/* Scrollable Table */}
              <Box ref={tableRef} sx={{ overflow: 'hidden', marginTop: '16px' }}>
                <StyledTable
                  columns={siteServiceHistoryColumns}
                  dataSource={filteredSiteHistories}
                  loading={eventSitesLoading}
                  pagination={false}
                  scroll={{ y: tableHeight }}
                  sticky
                />
              </Box>
            </Tabs.TabPane>
          </Tabs>
        </Card>
      </Box>

      <Modal
        open={cancelSitesOpen}
        title="Cancel Sites"
        onOk={confirmCancelSites}
        onCancel={() => setCancelSitesOpen(false)}
        okText="Yes, Cancel Sites"
        cancelText="Close"
        confirmLoading={cancelLoading}
        okButtonProps={{
          disabled: cancelLoading,
        }}
        cancelButtonProps={{
          disabled: cancelLoading,
        }}
      >
        <br/>
        {selectedSites.length === 0 ? (
          <p>No sites selected to cancel.</p>
        ) : (
          <Form form={cancelSitesForm} layout="vertical">
            {selectedSites.map((site) => (
              <div
                key={site.id}
                style={{ borderBottom: '1px solid #eee', marginBottom: '16px', paddingBottom: '8px' }}
              >
                <strong>{site.site_name}</strong>

                {site.cancelled_at ? (
                  <p style={{ color: 'gray' }}>
                    <em>Site already cancelled at {formatUtcDateTime(site.cancelled_at)}.</em>
                  </p>
                ) : site.cancellation_notified_at ? (
                  <p style={{ color: 'gray' }}>
                    Cancellation letter already sent on {formatUtcDateTime(site.cancellation_notified_at)}.
                  </p>
                ) : site.notified_at ? (
                  // Site was notified but does NOT have `cancellation_notified_at`.
                  // So we show a checkbox to optionally send a new cancellation letter.
                  <Form.Item
                    name={`sendCancellation_${site.id}`}
                    valuePropName="checked"
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                      <span>Send cancellation letter to {site.client_name}?</span>
                      <Checkbox />
                    </div>
                  </Form.Item>
                ) : (
                  <p style={{ marginTop: '8px' }}>
                    This site was never notified of service; no cancellation letter needed.
                  </p>
                )}
              </div>
            ))}
          </Form>
        )}

        {(selectedEvent?.event_type === constants?.event_type.snow) && cancelSitesWillArchiveSubs.length > 0 && (
          <div>
            <p style={{ marginTop: '16px', color: 'red' }}>
              {selectedSites.length > 1
                ? `Note: Cancelling these sites will archive the following subcontractor${cancelSitesWillArchiveSubs.length > 1 ? "s'" : "'s"} Yeti route${cancelSitesWillArchivePlural}:`
                : "Note: Cancelling this site will archive the following subcontractor's Yeti route:"
              }
              {cancelSitesWillArchiveSubs.length > 0 && (
                <ul>
                  {cancelSitesWillArchiveSubs.map((s, index) => (
                    <li key={index}>{s}</li>
                  ))}
                </ul>
              )}
            </p>
            <br/>
          </div>
        )}
        {(selectedEvent?.event_type === constants?.event_type.salt) && cancelSitesWillArchiveSubs.length > 0 && (
          <div>
            <p style={{ marginTop: '16px', color: 'red' }}>
              {selectedSites.length > 1
                ? "Note: Cancelling these sites will archive the subcontractor's Yeti route."
                : "Note: Cancelling this site will archive the subcontractor's Yeti route."
              }
            </p>
            <br/>
          </div>
        )}
      </Modal>

      {/* Redispatch Confirmation Modal */}
      <Modal
        title="Confirm Redispatch"
        open={redispatchConfirmOpen}
        onOk={handleRedispatch}
        onCancel={() => setRedispatchConfirmOpen(false)}
        okText="Yes, Redispatch"
        cancelText="Cancel"
        confirmLoading={redispatchLoading}
        okButtonProps={{
          disabled: redispatchLoading,
        }}
        cancelButtonProps={{
          disabled: redispatchLoading,
        }}
      >
        <p>Are you sure you want to redispatch this route?</p>
      </Modal>

      {/* Archive Confirmation Modal */}
      <Modal
        title="Confirm Archive"
        open={archiveConfirmOpen}
        onOk={handleArchive}
        onCancel={() => setArchiveConfirmOpen(false)}
        okText="Yes, Archive"
        cancelText="Cancel"
        confirmLoading={archiveLoading}
        okButtonProps={{
          disabled: archiveLoading,
        }}
        cancelButtonProps={{
          disabled: archiveLoading,
        }}
      >
        <p>Are you sure you want to archive {selectedDispatchRoute?.route_name}?</p>

        {archiveEvaluationData.subcontractors.length > 0 && (
          <div>
            <h4>
              This action will archive {archiveEvaluationData.numSubcontractors} Yeti route{archiveEvaluationData.numSubcontractors > 1 ? 's' : ''} assigned to the following subcontractor{archiveEvaluationData.numSubcontractors > 1 ? 's' : ''}:
            </h4>
            <ul>
              {archiveEvaluationData.subcontractors.map((subcontractor, index) => (
                <li key={`subcontractor-${index}`}>{subcontractor}</li>
              ))}
            </ul>
          </div>
        )}

        {archiveEvaluationData.sites.length > 0 && (
          <div>
            <h4>The following site{archiveEvaluationData.numSites > 1 ? 's' : ''}/client{archiveEvaluationData.numSites > 1 ? 's' : ''} will be sent a cancellation letter if you choose to send it:</h4>
            <ul>
              {archiveEvaluationData.sites.map((site, index) => (
                <li key={`site-${index}`}>{site}</li>
              ))}
            </ul>
          </div>
        )}

        {archiveEvaluationData.sites.length > 0 && (
          <Form form={archiveForm} layout="vertical">
            <Form.Item name="sendCancellationNotice" valuePropName="checked">
              <Checkbox className="custom-checkbox">Send Cancellation Letter</Checkbox>
            </Form.Item>
          </Form>
        )}
      </Modal>
    </ConfigProvider>
  );
};

export default EventHistoryPage;